import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '@/context/AppContext';
import { SUPPORTED_IMAGE_TYPES, MAX_FILE_SIZE } from '@/utils/constants';
import styles from '@/styles/components/PhotoUploadStep.module.css';

export default function PhotoUploadStep() {
  const { t } = useTranslation();
  const { state, dispatch } = useApp();
  const [preview, setPreview] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): boolean => {
    if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
      alert(t('step1Alert'));
      return false;
    }
    
    if (file.size > MAX_FILE_SIZE) {
      alert('文件大小不能超过10MB / File size cannot exceed 10MB');
      return false;
    }
    
    return true;
  }, [t]);

  const handleFile = useCallback((file: File) => {
    if (validateFile(file)) {
      dispatch({ type: 'SET_FILE', payload: file });
      setPreview(URL.createObjectURL(file));
    }
  }, [dispatch, validateFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  const handleNext = useCallback(() => {
    dispatch({ type: 'SET_STEP', payload: 2 });
  }, [dispatch]);

  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepTitle}>{t('step1Title')}</h2>
      
      <div
        className={`${styles.uploadArea} ${isDragOver ? styles.dragOver : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className={styles.uploadText}>{t('step1DragDrop')}</div>
        <div className={styles.supportText}>{t('step1Supports')}</div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className={styles.hiddenInput}
        />
      </div>

      {preview && (
        <div className={styles.previewContainer}>
          <img src={preview} alt="Preview" className={styles.previewImage} />
        </div>
      )}

      <div className={styles.instructionsTitle}>{t('step1BestResults')}</div>
      <ul className={styles.instructionsList}>
        <li className={styles.instructionItem}>{t('step1Instruction1')}</li>
        <li className={styles.instructionItem}>{t('step1Instruction2')}</li>
        <li className={styles.instructionItem}>{t('step1Instruction3')}</li>
      </ul>

      <div className={styles.buttonGroup}>
        <div></div>
        <button
          className="button"
          onClick={handleNext}
          disabled={!state.file}
        >
          {t('buttonNext')}
        </button>
      </div>
    </div>
  );
}